import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/shadcn-ui/card';
import { Button } from '@/components/shadcn-ui/button';
import { Badge } from '@/components/shadcn-ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/shadcn-ui/tabs';
import { ChevronLeft, MapPin, User, Building, FileText, Shield, Mail, Key, CheckCircle, XCircle, Clock, Folder, Trash, Phone } from 'lucide-react';
import { appAxios } from '@/network/base/appAxios';
import PermitStatusBadge from '@/components/PermitStatusBadge';
import { Textarea } from '@/components/shadcn-ui/textarea';
import { AdminChatHistory } from '@/components/chat/AdminChatHistory';
import { ExtractedDataView } from '@/components/chat/ExtractedDataView';
import ActivityLog from '@/components/ActivityLog';
import CountyPayment from '@/components/CountyPayment';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/shadcn-ui/dropdown-menu';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/shadcn-ui/dialog';


const formatDate = (dateString, includeTime = true) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (includeTime) {
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric'
        })
    } else {
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        })

    }
};

const StatusTypes = {
    "in_review": "In Review",
    "pending_payment": "Pending Payment",
    "payment_received": "Payment Received",
    "permit_processing": "Permit Processing",
    "permit_ready": "Permit Ready",
    "completed": "Completed",
    "on_hold": "On Hold",
    "rejected": "Rejected"
}

const blockingStatusChange = async (status) => {
    const notAllowedStatuses = [
        // "new",
        // "added_to_process_queue",
        // "added_to_signup_queue",
        // "added_to_profile_queue",
        // "signup_automation_started",
        // "profile_automation_started",
        "automation_started",

    ];
    if (notAllowedStatuses.includes(status)) {
        return true;
    }
    return false;
}

const getPortalProof = async (key) => {
    try {
        const response = await appAxios.get(`/Admin/v1.0/portal-proof-document`, {
            params: {
                key: key,
            }
        });
        window.open(response.data?.data?.viewUrl, '_blank');
    } catch (error) {
        console.error('Error fetching portal proof document:', error);
        toast.error('Failed to fetch portal proof document', {
            description: error.response?.data?.message || error.message || 'An error occurred'
        });
        return null;
    }
}

const getPermitType = type => {
    const types = {
        '0': 'Bldg Alter Repair',
        '1': 'Electrical Vehicle Charger',
        '2': 'Bldg Alter Repair',
        '3': 'Fire Sprinkler',
        '4': 'HVAC',
        '5': 'Plumbing',
        '6': 'Pressure Vessel',
        '7': 'Temporary Special Event',
    };
    return types[type] || `${type}`;
};


const RequestInfo = ({ permitDetails }) => {
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pr-2">
            {/* Customer Section */}
            <div className='border-b border-gray-200 pb-4'>
                <div className="flex items-center gap-2 mb-4">
                    <User className="h-5 w-5 text-muted-foreground" />
                    <h3 className="text-lg font-semibold">Customer</h3>
                </div>
                <div className="space-y-3 pl-7">
                    <div>
                        <div className="text-sm text-muted-foreground">Name</div>
                        <div className="font-medium">{permitDetails?.user_id?.fullname || 'N/A'}</div>
                    </div>
                    <div>
                        <div className="text-sm text-muted-foreground">Email</div>
                        <div className="font-medium">{permitDetails?.user_id?.user_email || 'N/A'}</div>
                    </div>
                    <div>
                        <div className="text-sm text-muted-foreground">Unique Portal Email</div>
                        <div className="font-medium">{permitDetails?.user_id?.unique_portal_email || 'N/A'}</div>
                    </div>
                </div>
            </div>

            {/* Site Section */}
            <div className='border-b border-gray-200 pb-4'>
                <div className="flex items-center gap-2 mb-4">
                    <MapPin className="h-5 w-5 text-muted-foreground" />
                    <h3 className="text-lg font-semibold">Site</h3>
                </div>
                <div className="space-y-3 pl-7">
                    <div>
                        <div className="text-sm text-muted-foreground">Address</div>
                        <div className="font-medium">{permitDetails?.portal_address || 'N/A'}</div>
                    </div>
                    <div>
                        <div className="text-sm text-muted-foreground">Unit</div>
                        <div className="font-medium">{permitDetails?.unit || 'N/A'}</div>
                    </div>
                    <div>
                        <div className="text-sm text-muted-foreground">Portal Name</div>
                        <div className="font-medium">{permitDetails?.portal_name || 'N/A'}</div>
                    </div>
                </div>
            </div>

            {/* Project Section */}
            <div className='border-b border-gray-200 pb-4'>
                <div className="flex items-center gap-2 mb-4">
                    <Building className="h-5 w-5 text-muted-foreground" />
                    <h3 className="text-lg font-semibold">Project</h3>
                </div>
                <div className="space-y-3 pl-7">
                    <div>
                        <div className="text-sm text-muted-foreground">Name</div>
                        <div className="font-medium">{permitDetails?.project_name || 'N/A'}</div>
                    </div>
                    <div>
                        <div className="text-sm text-muted-foreground">Project ID</div>
                        <div className="font-medium text-xs">{permitDetails?.project_id?.slice(-8) || 'N/A'}</div>
                    </div>
                    <div>
                        <div className="text-sm text-muted-foreground">Work Description</div>
                        <div className="font-medium">{permitDetails?.work_description || 'N/A'}</div>
                    </div>
                </div>
            </div>

            {/* Permit Section */}
            <div className='border-b border-gray-200 pb-4'>
                <div className="flex items-center gap-2 mb-4">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <h3 className="text-lg font-semibold">Permit</h3>
                </div>
                <div className="space-y-3 pl-7">
                    <div>
                        <div className="text-sm text-muted-foreground">Type</div>
                        <div className="font-medium">{getPermitType(permitDetails?.permit_type)}</div>
                    </div>
                    <div>
                        <div className="text-sm text-muted-foreground">Execution IP</div>
                        <div className="font-medium">{permitDetails?.ip_name || 'N/A'}</div>
                    </div>
                    <div>
                        <div className="text-sm text-muted-foreground">Application ID</div>
                        <div className="font-medium">{permitDetails?.application_id || 'Not assigned'}</div>
                    </div>
                    {/* <div>
                        <div className="text-sm text-muted-foreground">Ready to Fill</div>
                        <div className="font-medium">{permitDetails?.ready_to_fill ? 'Yes' : 'No'}</div>
                    </div>
                    <div>
                        <div className="text-sm text-muted-foreground">Submit to City</div>
                        <div className="font-medium">{permitDetails?.submit_to_city ? 'Yes' : 'No'}</div>
                    </div> */}
                    <div>
                        <div className="text-sm text-muted-foreground">Created</div>
                        <div className="font-medium">{formatDate(permitDetails?.createdAt)}</div>
                    </div>
                    <div>
                        <div className="text-sm text-muted-foreground">Last Updated</div>
                        <div className="font-medium">{formatDate(permitDetails?.updatedAt)}</div>
                    </div>
                </div>
            </div>

            {/* Owner Details Section */}
            {permitDetails?.customerDetails && (
                <div className='border-b border-gray-200 pb-4'>
                    <div className="flex items-center gap-2 mb-4">
                        <Phone className="h-5 w-5 text-muted-foreground" />
                        <h3 className="text-lg font-semibold">Owner Details</h3>
                    </div>
                    <div className="space-y-3 pl-7">
                        <div>
                            <div className="text-sm text-muted-foreground">Name</div>
                            <div className="font-medium">{permitDetails.customerDetails.owner_name || 'N/A'}</div>
                        </div>
                        <div>
                            <div className="text-sm text-muted-foreground">Email</div>
                            <div className="font-medium">{permitDetails.customerDetails.owner_email || 'N/A'}</div>
                        </div>
                        <div>
                            <div className="text-sm text-muted-foreground">Phone</div>
                            <div className="font-medium">{permitDetails.customerDetails.owner_phone || 'N/A'}</div>
                        </div>
                        <div>
                            <div className="text-sm text-muted-foreground">Property Address</div>
                            <div className="font-medium">{permitDetails.customerDetails.property_address || 'N/A'}</div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )

}

const PortalCreds = ({ permitDetails }) => {
    return (
        <div className="pr-2 space-y-4">
            {permitDetails?.user_id?.portal_creds && permitDetails.user_id.portal_creds.length > 0 ? (
                permitDetails.user_id.portal_creds.map((cred, index) => (
                    <Card key={cred._id || index} className="overflow-hidden border">
                        <CardHeader className="bg-muted/50 pb-3">
                            <CardTitle className="flex items-center gap-2 text-base">
                                <Folder className="h-5 w-5" />
                                {cred.portal_name.toUpperCase()}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4 space-y-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Portal Email */}
                                <div className="flex items-start gap-3">
                                    <Mail className="h-4 w-4 mt-1 text-muted-foreground" />
                                    <div>
                                        <div className="text-sm text-muted-foreground">Portal Email</div>
                                        <div className="font-medium text-sm break-all">{cred.portal_mail || 'N/A'}</div>
                                    </div>
                                </div>

                                {/* Portal Password */}
                                <div className="flex items-start gap-3">
                                    <Key className="h-4 w-4 mt-1 text-muted-foreground" />
                                    <div>
                                        <div className="text-sm text-muted-foreground">Portal Password</div>
                                        <div className="font-medium text-sm font-mono">{cred.portal_password || 'N/A'}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Status Indicators */}
                            <div className="pt-2 border-t">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {/* Signup Status */}
                                    <div className="flex items-center gap-2">
                                        {cred.signup_status === 'success' ? (
                                            <CheckCircle className="h-4 w-4 text-green-500" />
                                        ) : cred.signup_status === 'pending' ? (
                                            <Clock className="h-4 w-4 text-yellow-500" />
                                        ) : (
                                            <XCircle className="h-4 w-4 text-red-500" />
                                        )}
                                        <div>
                                            <div className="text-xs text-muted-foreground">Signup Status</div>
                                            <div className="text-sm font-medium capitalize">{cred.signup_status || 'N/A'}</div>
                                        </div>
                                    </div>

                                    {/* Profile Status */}
                                    <div className="flex items-center gap-2">
                                        {cred.profile_status === 'success' ? (
                                            <CheckCircle className="h-4 w-4 text-green-500" />
                                        ) : cred.profile_status === 'pending' ? (
                                            <Clock className="h-4 w-4 text-yellow-500" />
                                        ) : (
                                            <XCircle className="h-4 w-4 text-red-500" />
                                        )}
                                        <div>
                                            <div className="text-xs text-muted-foreground">Profile Status</div>
                                            <div className="text-sm font-medium capitalize">{cred.profile_status || 'N/A'}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Screenshots if available */}
                            {(cred.signup_screenshot || cred.profile_screenshot) && (
                                <div className="pt-2 border-t">
                                    <div className="text-xs text-muted-foreground mb-2">Screenshots</div>
                                    <div className="flex gap-2">
                                        {cred.signup_screenshot && (
                                            <button onClick={() => getPortalProof(cred.signup_screenshot)}>
                                                <Badge variant="outline" className="text-xs">Signup Available</Badge>
                                            </button>
                                        )}
                                        {cred.profile_screenshot && (
                                            <button onClick={() => getPortalProof(cred.profile_screenshot)}>
                                                <Badge variant="outline" className="text-xs">Profile Available</Badge>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                ))
            ) : (
                <div className="text-muted-foreground text-center py-8">
                    No portal credentials available
                </div>
            )}
        </div>
    )
}

const PermitDetail = () => {
    const { permitId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [statusReason, setStatusReason] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [chatHistory, setChatHistory] = useState([]);
    const [permitDetails, setPermitDetails] = useState(null);
    const [activityLog, setActivityLog] = useState([]);
    const [timelineStats, setTimelineStats] = useState(null);
    const [blockStatusChange, setBlockStatusChange] = useState(false);
    const [scriptExecutionData, setScriptExecutionData] = useState([]);
    const [scriptExecutionLoading, setScriptExecutionLoading] = useState(false);
    const [selectedScreenshot, setSelectedScreenshot] = useState(null);
    useEffect(() => {
        getPermitDetails();
        getChatHistory(permitId);
        getTimelineStats(permitId);
    }, [permitId]);

    const getChatHistory = async (permitId) => {
        try {
            const response = await appAxios.get(`/Admin/v1.0/chat-history`, {
                params: {
                    permitId: permitId,
                },
            });
            if (response.data?.data) {
                setChatHistory(response.data.data);
            } else {
                console.error('No data returned from API, using static data');
                setChatHistory([]);
            }
        } catch (error) {
            console.error('Error fetching chat history:', error);
            toast.error('Failed to fetch chat history', {
                description: error.response?.data?.message || error.message || 'An error occurred while fetching chat history'
            });
        } finally {
            setLoading(false);
        }
    };;

    const getActivityLog = async (permitId) => {
        try {
            const response = await appAxios.get(`/Admin/v1.0/activity`, {
                params: {
                    permitId: permitId,
                },
            });
            if (response.data?.data) {
                setActivityLog(response.data.data);
            } else {
                console.error('No data returned from API, using static data');
                setActivityLog([]);
            }
        } catch (error) {
            console.error('Error fetching activity log:', error);
            toast.error('Failed to fetch activity log', {
                description: error.response?.data?.message || error.message || 'An error occurred while fetching activity log'
            });
        } finally {
            setLoading(false);
        }
    };;

    const getScriptExecution = async (permitId) => {
        try {
            setScriptExecutionLoading(true);
            const response = await appAxios.get(`/Admin/v1.0/getAdminPermitProgressByPermitId`, {
                params: {
                    permitId: permitId,
                },
            });
            if (response.data?.data) {
                setScriptExecutionData(response.data.data);
            } else {
                setScriptExecutionData([]);
            }
        } catch (error) {
            console.error('Error fetching script execution:', error);
            toast.error('Failed to fetch script execution data', {
                description: error.response?.data?.message || error.message || 'An error occurred'
            });
            setScriptExecutionData([]);
        } finally {
            setScriptExecutionLoading(false);
        }
    };


    const getTimelineStats = async (permitId) => {
        try {
            const response = await appAxios.get(`/Admin/v1.0/timeline-stats`, {
                params: {
                    permitId: permitId,
                },
            });
            if (response.data?.data) {
                setTimelineStats(response.data.data);
            } else {
                console.error('No data returned from API, using static data');
                setTimelineStats(null);
            }
        } catch (error) {
            console.error('Error fetching timeline stats:', error);
            toast.error('Failed to fetch timeline stats', {
                description: error.response?.data?.message || error.message || 'An error occurred while fetching timeline stats'
            });
        } finally {
            setLoading(false);
        }
    };

    const getPermitDetails = async () => {
        try {
            const response = await appAxios.get(`/Admin/v1.0/permit-details`, {
                params: {
                    permitId: permitId,
                }
            });
            if (response.data?.data) {
                setPermitDetails(response.data.data);
                setBlockStatusChange(await blockingStatusChange(response.data.data?.permit_status));
            } else {
                console.error('No data returned from API, using static data');
                setPermitDetails(null);
                setBlockStatusChange(false);
            }
        }
        catch (error) {
            console.error('Error fetching permit details:', error);
            toast.error('Failed to fetch permit details', {
                description: error.response?.data?.message || error.message || 'An error occurred while fetching permit details'
            });
            setPermitDetails(null);
            setBlockStatusChange(false);
        }
        finally {
            setLoading(false);
        }
    };

    const handleUpdateStatus = async (permitId) => {
        if (!selectedStatus || !statusReason.trim()) {
            toast.warning('Missing information', {
                description: 'Please select a status and provide a reason'
            });
            return;
        }
        try {
            await appAxios.post(`/Admin/v1.0/update-permit-status/${permitId}`, {
                permitStatus: selectedStatus,
                message: statusReason,
            });
            toast.success('Status updated successfully', {
                description: `Permit status changed to ${selectedStatus}`
            });
            setStatusReason('');
            setSelectedStatus('');
            getPermitDetails();
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error('Failed to update status', {
                description: error.response?.data?.message || error.message || 'An error occurred while updating status'
            });
            if (error.response?.data?.message === 'Permit status is already the same as the new status') {
                getPermitDetails();
            }
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="text-muted-foreground">Loading permit details...</div>
            </div>
        );
    }

    if (!permitDetails) {
        return (
            <div className="flex flex-col items-center justify-center h-96">
                <div className="text-muted-foreground mb-4">Permit not found</div>
                <Button onClick={() => navigate('/admin/dashboard/requests')}>
                    Back to Requests
                </Button>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full overflow-hidden">
            {/* Header with Back Button */}
            <div className="flex items-center gap-3 flex-shrink-0 mb-6">
                <Button
                    variant="ghost"
                    size="icon"
                    className="dark bg-background text-white hover:bg-background/80 rounded-full"
                    onClick={() => navigate('/admin/dashboard/requests')}
                >
                    <ChevronLeft className="h-10 w-10" />
                </Button>
                <div className="flex-1">
                    <h1 className="text-3xl font-bold tracking-tight">
                        Permit {permitDetails?._id.slice(-8)}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        <Link to="/admin/dashboard/requests" className="hover:underline">
                            {permitDetails?.user_id?.fullname || 'N/A'}
                        </Link>
                        {' / '}
                        {permitDetails.portal_address || 'N/A'}
                        {' / '}
                        {permitDetails.project_name || 'N/A'}
                        {' / '}
                        {permitDetails.application_id || permitDetails._id.slice(-8)}
                    </p>
                </div>
            </div>

            {/* Status Badge */}

            <div className="flex items-center gap-2 flex-shrink-0 mb-6">
                <PermitStatusBadge status={permitDetails?.permit_status || 'new'} />
                {permitDetails?.user_type && (
                    <Badge variant="warning" className="capitalize text-xs font-medium">
                        {permitDetails.user_type}
                    </Badge>
                )}
            </div>

            {/* User Type */}


            {/* Main Content Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1 min-h-0">
                {/* Left Column - Main Content */}
                <div className="lg:col-span-2 flex flex-col min-h-0">
                    {/* Tabs Card - Fixed height */}
                    <Card className="flex flex-col flex-1 min-h-0 overflow-hidden">
                        <CardContent className="p-6 flex flex-col flex-1 min-h-0 overflow-hidden">
                            <Tabs defaultValue="request-info" className="flex flex-col flex-1 min-h-0 overflow-hidden">
                                {/* Tab List - Fixed at top */}
                                <TabsList className="w-full justify-start flex-shrink-0 mb-0 overflow-x-auto no-scrollbar">
                                    <TabsTrigger value="request-info">Request Info</TabsTrigger>
                                    <TabsTrigger value="chat-history">Chat History</TabsTrigger>
                                    <TabsTrigger value="agent-output">Agent Output</TabsTrigger>
                                    <TabsTrigger value="portal-creds">Portal Creds</TabsTrigger>
                                    <TabsTrigger value="documents">Documents</TabsTrigger>
                                    <TabsTrigger value="activity-log" onClick={() => getActivityLog(permitId)}>Activity Log</TabsTrigger>
                                    <TabsTrigger value="county-payment">Payment</TabsTrigger>
                                    <TabsTrigger value="script-execution" onClick={() => getScriptExecution(permitId)}>Script Execution</TabsTrigger>
                                </TabsList>

                                {/* Tab Content - Scrollable */}
                                <TabsContent value="request-info" className="mt-6 flex-1 overflow-y-auto min-h-0 no-scrollbar">
                                    <RequestInfo permitDetails={permitDetails} />
                                </TabsContent>

                                <TabsContent value="chat-history" className="mt-6 flex-1 overflow-y-auto min-h-0 no-scrollbar">
                                    <div className="pr-2">
                                        <AdminChatHistory chatHistory={chatHistory} />
                                    </div>
                                </TabsContent>

                                <TabsContent value="agent-output" className="mt-6 flex-1 overflow-y-auto min-h-0 no-scrollbar">
                                    <div className="pr-2">
                                        <ExtractedDataView
                                            data={chatHistory.find(msg => msg.done && msg.metadata)?.metadata}
                                            title="Extracted Data"
                                        />
                                    </div>
                                </TabsContent>

                                <TabsContent value="portal-creds" className="mt-6 flex-1 overflow-y-auto min-h-0 no-scrollbar">
                                    <PortalCreds permitDetails={permitDetails} />
                                </TabsContent>

                                <TabsContent value="documents" className="mt-6 flex-1 overflow-y-auto min-h-0">
                                    <div className="text-muted-foreground py-8 text-center">
                                        Documents will be displayed here
                                    </div>
                                </TabsContent>

                                <TabsContent value="activity-log" className="mt-6 flex-1 overflow-y-auto min-h-0 no-scrollbar">
                                    <ActivityLog activities={activityLog} />
                                </TabsContent>

                                <TabsContent value="county-payment" className="mt-6 flex-1 overflow-y-auto min-h-0 no-scrollbar">
                                    <CountyPayment permitDetails={permitDetails} onUpdate={getPermitDetails} />
                                </TabsContent>

                                <TabsContent value="script-execution" className="mt-6 flex-1 overflow-y-auto min-h-0 no-scrollbar">
                                    <div className="pr-2">
                                        {scriptExecutionLoading ? (
                                            <div className="flex items-center justify-center py-8">
                                                <div className="text-muted-foreground">Loading script execution...</div>
                                            </div>
                                        ) : scriptExecutionData.length > 0 ? (
                                            <div className="flex gap-4 overflow-x-auto pb-4">
                                                {scriptExecutionData.map((step, index) => (
                                                    step.screenshot && (
                                                        <div
                                                            key={step._id || index}
                                                            onClick={() => setSelectedScreenshot(step.screenshot)}
                                                            className="flex-shrink-0 w-64 h-48 cursor-pointer rounded-lg overflow-hidden border border-gray-700 hover:border-gray-500 transition-all"
                                                        >
                                                            <img
                                                                src={step.screenshot}
                                                                alt={step.step_name || `Step ${index + 1}`}
                                                                className="w-full h-full object-cover"
                                                            />
                                                        </div>
                                                    )
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-center text-muted-foreground py-8">
                                                No script execution data available
                                            </div>
                                        )}
                                    </div>
                                </TabsContent>
                            </Tabs>
                        </CardContent>
                    </Card>

                    {/* Screenshot Modal */}
                    <Dialog open={!!selectedScreenshot} onOpenChange={() => setSelectedScreenshot(null)}>
                        <DialogContent className="w-[95vw] max-w-5xl max-h-[90vh] bg-background border-gray-700 p-0 overflow-hidden text-black">
                            <DialogHeader className="px-4 py-3 sm:p-6">
                                <DialogTitle className="text-base sm:text-xl text-white">Script Execution Screenshot</DialogTitle>
                            </DialogHeader>
                            <div className="px-4 pb-4 sm:px-6 sm:pb-6">
                                <div className="bg-background rounded-lg overflow-y-auto max-h-[70vh] no-scrollbar">
                                    <img src={selectedScreenshot} alt="Script Execution" className="w-full h-auto" />
                                </div>
                            </div>
                        </DialogContent>
                    </Dialog>
                </div>

                {/* Right Column - Sidebar */}
                <div className="flex flex-col gap-6 min-h-0 max-h-full overflow-y-auto pr-2 no-scrollbar">
                    {/* Update Status Card */}
                    <Card className="flex-shrink-0">
                        <CardHeader>
                            <div className="flex items-center gap-2 justify-between">
                                <CardTitle className="text-lg">Update Status</CardTitle>


                                <div className="flex items-center gap-2 text-sm">
                                    <Button variant="outline" className="w-full justify-between" onClick={() => { setSelectedStatus(''); setStatusReason('') }}
                                        disabled={!selectedStatus && !statusReason.trim()}

                                    >
                                        <Trash className="h-4 w-4" />
                                        Clear
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {blockStatusChange ?
                                <div className="text-sm font-medium mb-2 block">
                                    {`Pemrit is yet to process in automation queue`}
                                </div> :
                                (
                                    <>
                                        <div>
                                            <label className="text-sm font-medium mb-2 block">
                                                Current Status
                                            </label>
                                            <Badge variant="outline" className="w-full h-10 rounded-md text-sm">
                                                {permitDetails?.permit_status ? permitDetails?.permit_status?.split('_').join(' ').charAt(0).toUpperCase() + permitDetails?.permit_status.split('_').join(' ').slice(1) : 'New'}
                                            </Badge>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium mb-2 block">New Status</label>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="outline" className="w-full justify-between">
                                                        {StatusTypes[selectedStatus] || 'Select status...'}
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent className="w-full">
                                                    {Object.entries(StatusTypes).map(([key, value]) => (
                                                        <DropdownMenuItem
                                                            key={key}
                                                            disabled={key === permitDetails?.permit_status}
                                                            onClick={() => setSelectedStatus(key)}
                                                        >
                                                            {value}
                                                        </DropdownMenuItem>
                                                    ))}

                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium mb-2 block">
                                                Reason (required)
                                            </label>
                                            <Textarea
                                                placeholder="Enter reason for status change..."
                                                value={statusReason}
                                                onChange={(e) => setStatusReason(e.target.value)}
                                                rows={4}
                                            />
                                        </div>
                                        <Button
                                            className="w-full"
                                            onClick={() => handleUpdateStatus(permitDetails._id)}
                                            disabled={!selectedStatus || !statusReason.trim()}
                                        >
                                            Update Status
                                        </Button>
                                    </>
                                )}
                        </CardContent>
                    </Card>

                    {/* Timeline Card */}
                    <Card className="flex-shrink-0">
                        <CardHeader>
                            <CardTitle className="text-lg">Timeline</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {timelineStats ? (
                                <>
                                    <div className="flex items-center justify-between py-2">
                                        <div className="text-sm text-muted-foreground">Submitted</div>
                                        <div className="text-sm font-medium">
                                            {formatDate(timelineStats.permitCreatedAt, false)}
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between py-2">
                                        <div className="text-sm text-muted-foreground">Last Updated</div>
                                        <div className="text-sm font-medium">
                                            {formatDate(timelineStats.permitUpdatedAt, false)}
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between py-2">
                                        <div className="text-sm text-muted-foreground">Status Changes</div>
                                        <div className="text-sm font-medium">
                                            {timelineStats.statusChange || 0}
                                        </div>
                                    </div>
                                    {/* <div className="flex items-center justify-between py-2">
                                        <div className="text-sm text-muted-foreground">Documents</div>
                                        <div className="text-sm font-medium">
                                            {timelineStats.documents || 2}
                                        </div>
                                    </div> */}
                                </>
                            ) : (
                                <div className="text-sm text-muted-foreground text-center py-4">
                                    Loading timeline...
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default PermitDetail;

